import { atom } from 'jotai';
import { useAtomValue, useSetAtom } from 'jotai/react';
import { useCallback, useMemo } from 'react';
import type { ViewModelId } from 'balus-common';

type AIAssistantRoute =
  | {
      page: 'chat';
    }
  | {
      page: 'settings';
    }
  | {
      page: 'list';
    }
  | {
      page: 'skill';
    };

const INITIAL_ROUTE: AIAssistantRoute = { page: 'list' };

type AIAssistantRouter = {
  push: (route: AIAssistantRoute) => void;
  replace: (route: AIAssistantRoute) => void;
  back: () => void;
};

// 表示中の画面は開いているビューモデルに紐づく状態なので viewModelId とペアで保持し、
// 別のビューモデルを開いたときは初期画面に戻す。
// stack は遷移履歴で、末尾が現在表示中の画面。先頭は常に初期画面
const internalRouteStackAtom = atom<{ viewModelId: ViewModelId; stack: AIAssistantRoute[] } | null>(null);

export const useAIAssistantRoute = (viewModelId: ViewModelId): AIAssistantRoute => {
  const state = useAtomValue(internalRouteStackAtom);
  if (state !== null && state.viewModelId === viewModelId && state.stack.length > 0) {
    return state.stack[state.stack.length - 1];
  }
  return INITIAL_ROUTE;
};

export const useAIAssistantRouter = (viewModelId: ViewModelId): AIAssistantRouter => {
  const setState = useSetAtom(internalRouteStackAtom);

  const push = useCallback(
    (route: AIAssistantRoute) => {
      setState((prev) => {
        const stack = prev !== null && prev.viewModelId === viewModelId ? prev.stack : [INITIAL_ROUTE];
        return { viewModelId, stack: [...stack, route] };
      });
    },
    [setState, viewModelId]
  );

  const replace = useCallback(
    (route: AIAssistantRoute) => {
      setState((prev) => {
        const stack = prev !== null && prev.viewModelId === viewModelId ? prev.stack : [INITIAL_ROUTE];
        return { viewModelId, stack: [...stack.slice(0, -1), route] };
      });
    },
    [setState, viewModelId]
  );

  const back = useCallback(() => {
    setState((prev) => {
      // 履歴が無い（未初期化 or 別ビューモデル）か初期画面にいる場合は戻る先がない
      if (prev === null || prev.viewModelId !== viewModelId || prev.stack.length <= 1) return prev;
      return { viewModelId, stack: prev.stack.slice(0, -1) };
    });
  }, [setState, viewModelId]);

  return useMemo(() => ({ push, replace, back }), [push, replace, back]);
};

type AIAssistantThreadRequest = {
  threadId: string;
  nonce: number;
};

// チャット画面で開くスレッドの指定。null は「最新スレッドを開く」を意味する。
// nonce は同じスレッドを再度選択したときにも変化し、AIAssistantChat を key で
// 確実に再マウントさせるための連番
const internalThreadRequestAtom = atom<({ viewModelId: ViewModelId } & AIAssistantThreadRequest) | null>(null);

export const useAIAssistantThreadRequest = (viewModelId: ViewModelId): AIAssistantThreadRequest | null => {
  const state = useAtomValue(internalThreadRequestAtom);
  return state !== null && state.viewModelId === viewModelId ? state : null;
};

export const useRequestAIAssistantThread = (viewModelId: ViewModelId): ((threadId: string) => void) => {
  const setState = useSetAtom(internalThreadRequestAtom);
  return useCallback(
    (threadId: string) => setState((prev) => ({ viewModelId, threadId, nonce: (prev?.nonce ?? 0) + 1 })),
    [setState, viewModelId]
  );
};
